import React from 'react';
import { FC130 } from '../../../services/c205Api';
import FieldError from '../../../components/FieldError';
import LookupField from '../../../components/LookupField';

interface MasterFieldsProps {
  formData: Partial<FC130>;
  isEditing: boolean;
  getField: (key: keyof FC130) => FC130[keyof FC130];
  setField: (key: keyof FC130, value: FC130[keyof FC130]) => void;
  getFieldError: (field: string) => string | undefined;
  // Computed values
  totalReturnQty: number;
  netAmount: number;
  taxAmount: number;
  totalWithTax: number;
}

/** Format number with locale grouping, max 2 decimals */
const fmt = (v: number | null | undefined): string => {
  if (v == null || v === 0) return '0';
  return v.toLocaleString('en-US', { maximumFractionDigits: 2 });
};

const MasterFields: React.FC<MasterFieldsProps> = ({
  formData, isEditing,
  getField, setField, getFieldError,
  totalReturnQty, netAmount, taxAmount, totalWithTax,
}) => {
  return (
    <div className="c205-master-fields">

      {/* Row 1: 交運單號 + 客戶名稱 (FoxPro top≈39-70) */}
      <div className="form-section">
        <span className="section-tag">退回單資訊</span>
        <div className="form-grid">

          <label>交運單號</label>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <FieldError error={getFieldError('fc130_10')} />
            <input type="text" style={{ width: 120 }}
              readOnly={!isEditing}
              disabled={!isEditing}
              value={getField('fc130_10') as string}
              onChange={e => setField('fc130_10', e.target.value)} data-contract-field="fc130_10"
            />
          </div>

          <label data-contract-field="ui_customer_name">客戶名稱</label>
          <div>
            <span style={{ color: '#555' }}>{(formData as Record<string, unknown>).customer_name as string || '--'}</span>
          </div>

          <label>客訴單號</label>
          <div>
            <FieldError error={getFieldError('fc130_22')} />
            <LookupField
              lookupType="complaintOrder"
              value={(getField('fc130_22') as string) || ''}
              onChange={code => setField('fc130_22', code)}
              disabled={!isEditing}
              style={{ width: 120 }}
              maxLength={8}
              title="客訴單號查詢" contractFieldId="fc130_22"
            />
          </div>

          <label>入庫庫位</label>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <FieldError error={getFieldError('fc130_12')} />
            <LookupField
              lookupType="warehouse"
              value={(getField('fc130_12') as string) || ''}
              onChange={code => setField('fc130_12', code)}
              disabled={!isEditing}
              style={{ width: 80 }}
              maxLength={5}
              title="庫位查詢" contractFieldId="fc130_12"
            />
            <span style={{ color: '#555' }}>{(formData as Record<string, unknown>).warehouse_name as string || ''}</span>
          </div>

          <label>交運幣別</label>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <LookupField
              lookupType="currency"
              value={(getField('fc130_18') as string) || ''}
              onChange={code => setField('fc130_18', code)}
              disabled={true}
              style={{ width: 60 }}
              maxLength={3}
              title="幣別查詢" contractFieldId="fc130_18"
            />
          </div>

          <label data-contract-field="ui_exchange_rate">交運匯率</label>
          <div>
            <span style={{ color: '#555' }}>{(formData as Record<string, unknown>).exchange_rate as string || '--'}</span>
          </div>

        </div>
      </div>

      {/* Section 2: 金額匯總 (FoxPro top≈207-289) */}
      <div className="form-section">
        <span className="section-tag">金額匯總</span>
        <div className="form-grid">

          <label data-contract-field="fc130_13">總退回數量</label>
          <div>
            <input type="text" readOnly style={{ width: 120, textAlign: 'right' }}
              value={fmt(totalReturnQty)} />
          </div>

          <label data-contract-field="fc130_15">未稅銷退淨額</label>
          <div>
            <input type="text" readOnly style={{ width: 120, textAlign: 'right' }}
              value={fmt(netAmount)} />
          </div>

          <label>稅額</label>
          <div>
            <input type="text" readOnly style={{ width: 120, textAlign: 'right' }}
              value={fmt(taxAmount)} />
          </div>

          <label data-contract-field="ui_total_with_tax">含稅銷退總金額</label>
          <div>
            <span style={{ fontWeight: 'bold', fontSize: 14 }}>{fmt(totalWithTax)}</span>
          </div>

        </div>
      </div>

      {/* Section 3: 人員資訊 (FoxPro top≈269-295, right side) */}
      <div className="form-section">
        <span className="section-tag">人員資訊</span>
        <div className="form-grid">

          <label data-contract-field="ui_delivery_person">交運人員</label>
          <div>
            <span style={{ color: '#555' }}>{(formData as Record<string, unknown>).delivery_person as string || '--'}</span>
          </div>

          <label data-contract-field="fc130_91">輸單人員</label>
          <div>
            <span style={{ color: '#555' }}>{formData.fc130_91 || '--'}</span>
          </div>

        </div>
      </div>

    </div>
  );
};

export default React.memo(MasterFields);
